import * as React from 'react'
import { ScrollArea } from '~/sotano-kit/primitives/scroll-area'

export interface MentionsListProps<T> {
  items: T[]
  renderItem: (
    props: {
      item: T
      isSelected: boolean
      mouseEvents?: boolean
    } & React.HTMLAttributes<HTMLButtonElement>
  ) => React.ReactNode
  selectItem: (index: number) => void
  mouseEvents?: boolean
  onExit: () => void
  setSelectedIndex: (index: number) => void
  selectedIndex: number
  disabled: boolean
  focusItems?: boolean
}

export function MentionsListUI<T>({
  items,
  renderItem,
  selectedIndex,
  mouseEvents,
  disabled,
  onExit,
  selectItem,
  setSelectedIndex, // containerRef,
  focusItems,
}: MentionsListProps<T>) {
  const listRef = React.useRef<HTMLDivElement>(null)
  const [isPointerIdle, setIsPointerIdle] = React.useState(true)

  const selectedIndexRef = React.useRef(selectedIndex)
  // eslint-disable-next-line react-compiler/react-compiler
  selectedIndexRef.current = selectedIndex

  const itemsRef = React.useRef(items)
  // eslint-disable-next-line react-compiler/react-compiler
  itemsRef.current = items

  React.useEffect(() => {
    if (!listRef.current) return
    if (!isPointerIdle) return
    const item = listRef.current.querySelector<HTMLLIElement>(
      '[data-selected="true"]'
    )
    if (!item) return
    item.scrollIntoView({ block: 'nearest', behavior: 'instant' })
    // will focus the item because it conflicts with use-keyboard-navigation of the editor
    if (focusItems) item.focus({ preventScroll: true })
  }, [selectedIndex, isPointerIdle, focusItems])

  React.useEffect(() => {
    const handleMouseMove = () => {
      setIsPointerIdle(false)
    }
    const handleKeyDown = () => {
      setIsPointerIdle(true)
    }

    window.addEventListener('mousemove', handleMouseMove)
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('mousemove', handleMouseMove)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  React.useEffect(() => {
    if (disabled) return

    const handleKeyDown = (event: KeyboardEvent) => {
      event.stopPropagation()
      const target = event.target as HTMLElement
      if (target.getAttribute('data-scroll-container')) {
        if (event.key === 'ArrowUp') {
          event.preventDefault()
        }
        if (event.key === 'ArrowDown') {
          event.preventDefault()
        }
      }
      switch (event.key) {
        case 'ArrowUp': {
          const newIndex = selectedIndexRef.current - 1

          if (newIndex < 0) {
            // do nothing
            break
          }

          setSelectedIndex(newIndex)
          break
        }
        case 'ArrowDown': {
          const newIndex = selectedIndexRef.current + 1

          if (newIndex >= items.length) {
            // do nothing
            break
          }

          setSelectedIndex(newIndex)
          break
        }
        case 'Enter': {
          selectItem(selectedIndexRef.current)
          break
        }
        case 'Escape': {
          onExit?.()
          break
        }

        default:
          break
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [selectedIndex, onExit, items, selectItem, setSelectedIndex, disabled])

  const Render = renderItem
  return (
    <div className="!z-[51] relative not-prose rounded-md border bg-dark-bg border-l border-dark-border-solid min-w-[280px] max-w-[320px] flex flex-col overflow-hidden shadow-modal">
      <ScrollArea
        orientation="vertical"
        className="flex-1 *:!outline-none pointer-events-auto flex flex-col max-h-[200px]"
        viewportClassName="flex flex-col"
        ref={listRef}
        data-scroll-container
        data-inner-popover="true"
      >
        {items.length ? (
          items.map((item, index) => (
            <Render
              onClick={(e) => {
                e.stopPropagation()
                e.preventDefault()
                if (disabled) return
                selectItem(index)
              }}
              onPointerEnter={() => {
                if (isPointerIdle) return
                setSelectedIndex(index)
              }}
              key={
                typeof item === 'string'
                  ? item
                  : 'id' in (item as { id: string })
                    ? (item as { id: string }).id
                    : index
              }
              item={item}
              mouseEvents={mouseEvents}
              isSelected={index === selectedIndex}
              className="w-full"
            />
          ))
        ) : (
          <div className="px-2 py-1 text-xs text-dark-muted">No results</div>
        )}
      </ScrollArea>
    </div>
  )
}

export type MentionControllerProps<T> = {
  onExit: () => void
  items: T[]
  command: (item: T & { onExit: () => void }) => void
  renderItem: (
    props: {
      item: T
      isSelected: boolean
      mouseEvents?: boolean
    } & React.HTMLAttributes<HTMLButtonElement>
  ) => React.ReactNode
  focusItems?: boolean
}
export class MentionsListController<T> extends React.Component<
  MentionControllerProps<T>,
  { selectedIndex: number }
> {
  selectItem(index: number) {
    const item = this.props.items[index]
    if (item) {
      this.props.command({ ...item, onExit: this.props.onExit })
    }
  }

  constructor(props: MentionControllerProps<T>) {
    super(props)
    this.state = {
      selectedIndex: 0,
    }
    this.selectItem = this.selectItem.bind(this)
  }

  onKeyDown({ event }: { event: KeyboardEvent }) {
    if (event.key === 'ArrowUp') {
      return true
    }

    if (event.key === 'ArrowDown') {
      return true
    }

    if (event.key === 'Enter') {
      this.selectItem(this.state.selectedIndex)
      return true
    }

    return false
  }

  enterHandler() {
    this.selectItem(this.state.selectedIndex)
  }

  componentDidUpdate(
    prevProps: Readonly<MentionControllerProps<T>>
    // prevState: Readonly<{ selectedIndex: number }>,
    // snapshot?: any
  ): void {
    if (this.props.items !== prevProps.items) {
      this.setState({ selectedIndex: 0 })
    }
  }

  render() {
    const { items, renderItem } = this.props
    const { selectedIndex } = this.state

    const setSelectedIndex = (index: number) => {
      this.setState({ selectedIndex: index })
    }

    const Component = renderItem
    return (
      <MentionsListUI
        focusItems={this.props.focusItems}
        disabled={false}
        renderItem={Component}
        selectItem={this.selectItem}
        selectedIndex={selectedIndex}
        setSelectedIndex={setSelectedIndex}
        onExit={this.props.onExit}
        items={items}
      />
    )
  }
}
